import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { runService, webSocketService } from '../services';
import { RunWithDetails, RunStats } from '../types';
import { LoadingSpinner, Card, Badge, Button, Alert, Input } from '../components/ui';
import { AssignedLabs } from '../components/learner/AssignedLabs';

type RunStateFilter = 'all' | 'active' | 'graded' | 'destroyed';

const filterLabels: Record<RunStateFilter, string> = {
  all: 'All',
  active: 'Active',
  graded: 'Graded',
  destroyed: 'Destroyed',
};

export function MyLabs() {
  const navigate = useNavigate();
  
  const [runs, setRuns] = useState<RunWithDetails[]>([]);
  const [filteredRuns, setFilteredRuns] = useState<RunWithDetails[]>([]);
  const [stats, setStats] = useState<RunStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<RunStateFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [actionLoading, setActionLoading] = useState<Record<number, string>>({});
  const [wsConnected, setWsConnected] = useState(false);

  const loadRuns = useCallback(async () => {
    try {
      setError(null);
      const runsData = await runService.getRuns(filter);
      setRuns(runsData);
    } catch (err: any) {
      console.error('Failed to load runs:', err);
      setError('Failed to load lab runs. Please try again.');
    }
  }, [filter]);

  const loadStats = useCallback(async () => {
    try {
      const statsData = await runService.getRunStats();
      setStats(statsData);
    } catch (err: any) {
      console.error('Failed to load stats:', err);
    }
  }, []);

  const filterRuns = useCallback(() => {
    let filtered = [...runs];
    
    // Apply search filter (state filtering is now done server-side)
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(run => 
        run.lab.title.toLowerCase().includes(term) ||
        run.lab.slug.toLowerCase().includes(term) ||
        (run.lab.tags && run.lab.tags.some(tag => tag.toLowerCase().includes(term)))
      );
    }

    filtered.sort((a, b) => {
      const aTime = new Date(a.latest_grade?.created_at || a.updated_at || a.created_at).getTime();
      const bTime = new Date(b.latest_grade?.created_at || b.updated_at || b.created_at).getTime();
      return bTime - aTime;
    });
    
    setFilteredRuns(filtered);
  }, [runs, searchTerm]);

  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      await Promise.all([loadRuns(), loadStats()]);
      setLoading(false);
    };

    initializeData();
  }, [loadRuns, loadStats]);

  useEffect(() => {
    filterRuns();
  }, [filterRuns]);

  // WebSocket connection for real-time updates
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token && !wsConnected) {
      webSocketService.connect(token);
      setWsConnected(true);
    }

    const unsubscribeStateChange = webSocketService.onRunStateChanged((message) => {
      loadRuns();
      loadStats();
    });

    const unsubscribeGradeComplete = webSocketService.onGradeCompleted((message) => {
      loadRuns();
      loadStats();
    });

    const unsubscribeTTLExtend = webSocketService.onTTLExtended((message) => {
      loadRuns();
    });

    return () => {
      unsubscribeStateChange();
      unsubscribeGradeComplete();
      unsubscribeTTLExtend();
    };
  }, [wsConnected, loadRuns, loadStats]);

  const handleAction = async (runId: number, actionType: string) => {
    setActionLoading(prev => ({ ...prev, [runId]: actionType }));
    
    try {
      switch (actionType) {
        case 'connect':
          const run = runs.find(r => r.id === runId);
          if (run && run.ip_address) {
            const sshCommand = runService.getSSHCommand(run);
            navigator.clipboard.writeText(sshCommand);
            alert(`SSH command copied to clipboard:\n${sshCommand}`);
          }
          break;
          
        case 'grade':
          await runService.gradeRun(runId);
          await loadRuns();
          await loadStats();
          break;
          
        case 'extend':
          await runService.extendTTL(runId, { hours: 6 });
          await loadRuns();
          break;
          
        case 'destroy':
          if (window.confirm('Are you sure you want to destroy this lab? This action cannot be undone.')) {
            await runService.destroyRun(runId);
            await loadRuns();
            await loadStats();
          }
          break;
          
        case 'view':
          navigate(`/runs/${runId}`);
          break;

        case 'view-grade':
          navigate(`/runs/${runId}/grade`);
          break;
          
        default:
          console.warn('Unknown action:', actionType);
      }
    } catch (err: any) {
      console.error('Action failed:', err);
      let errorMessage = `Failed to ${actionType} lab. Please try again.`;
      
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }
      
      setError(errorMessage);
    } finally {
      setActionLoading(prev => ({ ...prev, [runId]: '' }));
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty.toLowerCase()) {
      case 'beginner':
        return 'bg-green-500';
      case 'intermediate':
        return 'bg-yellow-500';
      case 'advanced':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusColor = (state: string) => {
    return runService.getStatusColor(state);
  };

  const getStatusText = (state: string) => {
    return runService.getStatusText(state);
  };

  const inProgressRuns = stats?.in_progress_runs ?? ((stats?.active_runs || 0) + (stats?.pending_runs || 0));
  const visibleTotalRuns = stats?.visible_total_runs ?? stats?.total_runs ?? 0;
  const gradedRuns = stats?.graded_runs ?? 0;

  const getFilterCount = (state: RunStateFilter) => {
    if (!stats) return null;
    switch (state) {
      case 'all':
        return visibleTotalRuns;
      case 'active':
        return inProgressRuns;
      case 'graded':
        return gradedRuns;
      case 'destroyed':
        return stats.destroyed_runs;
      default:
        return null;
    }
  };

  const formatGrade = (run: RunWithDetails) => {
    if (!run.latest_grade) {
      return 'Not graded';
    }
    return `${Math.round(run.latest_grade.percentage)}%`;
  };

  if (loading) {
    return (
      <div className="p-8 text-white bg-gray-900 min-h-screen">
        <div className="flex items-center justify-center mt-20">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 text-white bg-gray-900 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-4">My Lab Instances</h1>
          <p className="text-gray-400">
            Review active labs, completed work, and grade reports.
          </p>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="error" className="mb-6">
            {error}
            <Button 
              onClick={() => setError(null)} 
              className="ml-4"
              size="sm"
              variant="secondary"
            >
              Dismiss
            </Button>
          </Alert>
        )}

        {/* Statistics Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-gray-800 border-gray-700">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-400">{inProgressRuns}</div>
                <div className="text-sm text-gray-400">Active Labs</div>
                <div className="text-xs text-gray-500 mt-1">Max: 2</div>
              </div>
            </Card>
            <Card className="bg-gray-800 border-gray-700">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-400">{gradedRuns}</div>
                <div className="text-sm text-gray-400">Graded Labs</div>
              </div>
            </Card>
            <Card className="bg-gray-800 border-gray-700">
              <div className="text-center">
                <div className="text-2xl font-bold text-white">{stats.destroyed_runs}</div>
                <div className="text-sm text-gray-400">Destroyed Labs</div>
              </div>
            </Card>
            <Card className="bg-gray-800 border-gray-700">
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-400">{visibleTotalRuns}</div>
                <div className="text-sm text-gray-400">Visible History</div>
              </div>
            </Card>
          </div>
        )}

        {/* Assigned Labs Section */}
        <div className="mb-8">
          <AssignedLabs 
            onLabLaunch={(labSlug) => {
              // Refresh runs when an assigned lab is launched
              loadRuns();
              loadStats();
            }}
          />
        </div>

        {/* Lab Instances Header */}
        <div className="mb-4">
          <h2 className="text-2xl font-bold text-white">My Lab Instances</h2>
          <p className="text-gray-400 text-sm">
            Your active, graded, and destroyed lab history
          </p>
        </div>

        {/* Filters and Search */}
        <Card className="bg-gray-800 border-gray-700 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            {/* State Filter */}
            <div className="flex space-x-2">
              {(['all', 'active', 'graded', 'destroyed'] as RunStateFilter[]).map((state) => (
                <Button
                  key={state}
                  onClick={() => setFilter(state)}
                  variant={filter === state ? 'primary' : 'secondary'}
                  size="sm"
                  data-testid={`filter-${state}`}
                >
                  {filterLabels[state]}
                  {getFilterCount(state) !== null && (
                    <span className="ml-2 text-xs opacity-75">{getFilterCount(state)}</span>
                  )}
                </Button>
              ))}
            </div>

            {/* Search */}
            <div className="flex-1 min-w-60">
              <Input
                type="text"
                placeholder="Search by lab title or tags..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>
          </div>
        </Card>

        {/* Run Cards */}
        {filteredRuns.length === 0 && !loading ? (
          <Alert variant="info">
            {runs.length === 0 
              ? "No lab instances found. Start a lab from the Lab Catalog to see it here."
              : "No labs match your current filters."
            }
          </Alert>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredRuns.map((run) => (
              <Card key={run.id} className="bg-gray-800 border-gray-700">
                <div className="space-y-4">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-2">
                        {run.lab.title}
                      </h3>
                      <div className="flex items-center space-x-2 mb-2">
                        <Badge className={`${getDifficultyColor(run.lab.difficulty)} text-white`}>
                          {run.lab.difficulty}
                        </Badge>
                        <Badge 
                          variant={getStatusColor(run.state) === 'success' ? 'success' : 
                                  getStatusColor(run.state) === 'error' ? 'danger' :
                                  getStatusColor(run.state) === 'processing' ? 'info' :
                                  getStatusColor(run.state) === 'warning' ? 'warning' : 'default'}
                        >
                          {getStatusText(run.state)}
                        </Badge>
                      </div>
                      <div className="text-sm text-gray-400">
                        Run #{run.id} • {new Date(run.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>

                  {/* Status Details */}
                  <div className="space-y-2">
                    {run.state === 'active' && run.ip_address && (
                      <div className="text-sm">
                        <span className="text-gray-400">IP Address:</span>
                        <span className="ml-2 font-mono text-green-400">{run.ip_address}</span>
                      </div>
                    )}
                    {run.state === 'active' && (
                      <div className="text-sm">
                        <span className="text-gray-400">Time Remaining:</span>
                        <span className="ml-2 text-yellow-400">
                          {runService.formatTimeRemaining(run.ttl_expires_at)}
                        </span>
                      </div>
                    )}
                    {run.lab.estimated_minutes && (
                      <div className="text-sm">
                        <span className="text-gray-400">Estimated Time:</span>
                        <span className="ml-2">{run.lab.estimated_minutes} min</span>
                      </div>
                    )}
                    <div className="text-sm">
                      <span className="text-gray-400">Latest Grade:</span>
                      <span className={`ml-2 font-medium ${run.latest_grade ? 'text-blue-300' : 'text-gray-500'}`}>
                        {formatGrade(run)}
                      </span>
                      {run.latest_grade && (
                        <span className="ml-2 text-xs text-gray-500">
                          {new Date(run.latest_grade.created_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Lab Summary */}
                  {run.lab.summary && (
                    <p className="text-sm text-gray-300 line-clamp-2">
                      {run.lab.summary}
                    </p>
                  )}

                  {/* Tags */}
                  {run.lab.tags && run.lab.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {run.lab.tags.slice(0, 3).map((tag) => (
                        <Badge key={tag} variant="default" size="sm">
                          {tag}
                        </Badge>
                      ))}
                      {run.lab.tags.length > 3 && (
                        <Badge variant="default" size="sm">
                          +{run.lab.tags.length - 3}
                        </Badge>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-700">
                    {runService.canSSH(run.state) && run.ip_address && (
                      <Button
                        onClick={() => handleAction(run.id, 'connect')}
                        disabled={actionLoading[run.id] === 'connect'}
                        size="sm"
                        variant="success"
                      >
                        {actionLoading[run.id] === 'connect' ? 'Copying...' : 'SSH'}
                      </Button>
                    )}
                    
                    {runService.canGrade(run.state) && (
                      <Button
                        onClick={() => handleAction(run.id, 'grade')}
                        disabled={actionLoading[run.id] === 'grade'}
                        size="sm"
                        variant="primary"
                      >
                        {actionLoading[run.id] === 'grade' ? 'Grading...' : 'Grade'}
                      </Button>
                    )}
                    
                    {runService.canExtendTTL(run.state) && (
                      <Button
                        onClick={() => handleAction(run.id, 'extend')}
                        disabled={actionLoading[run.id] === 'extend'}
                        size="sm"
                        variant="warning"
                      >
                        {actionLoading[run.id] === 'extend' ? 'Extending...' : '+6h'}
                      </Button>
                    )}
                    
                    <Button
                      onClick={() => handleAction(run.id, 'view')}
                      size="sm"
                      variant="secondary"
                    >
                      Details
                    </Button>

                    {run.latest_grade && (
                      <Button
                        onClick={() => handleAction(run.id, 'view-grade')}
                        size="sm"
                        variant="primary"
                        data-testid="view-grade-report-btn"
                      >
                        View Report
                      </Button>
                    )}
                    
                    {runService.canDestroy(run.state) && (
                      <Button
                        onClick={() => handleAction(run.id, 'destroy')}
                        disabled={actionLoading[run.id] === 'destroy'}
                        size="sm"
                        variant="danger"
                      >
                        {actionLoading[run.id] === 'destroy' ? 'Destroying...' : 'Destroy'}
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Debug Info (development only) */}
        {process.env.NODE_ENV === 'development' && runs.length > 0 && (
          <Card className="bg-gray-800 border-gray-700 mt-8">
            <h3 className="text-sm font-medium text-gray-400 mb-2">
              Debug Info ({runs.length} runs loaded, {filteredRuns.length} filtered):
            </h3>
            <pre className="text-xs text-gray-500 overflow-x-auto">
              {JSON.stringify({ 
                stats,
                filter,
                searchTerm,
                wsConnected,
                runStates: runs.map(r => ({
                  id: r.id,
                  state: r.state,
                  title: r.lab.title,
                  latestGrade: r.latest_grade?.percentage
                }))
              }, null, 2)}
            </pre>
          </Card>
        )}
      </div>
    </div>
  );
}
